import { AxiosResponse } from "axios";
import { GridColDef } from "@material-ui/data-grid";
import { Chip } from "@material-ui/core";
import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { IVersionedObject } from "../../../planEatApi/PlanEatFacade";
import { SysadminData, useSysadminData } from "../SysadminData";
import { errorMessage, formatDateTime, parseServiceDate, safeDecode, SysadminSection, sysadminPaths } from "../sysadminUtils";
import { EntityTable } from "./EntityTable";
import { Crumb, PageHeader } from "./PageHeader";

export const sectionLabels: Record<SysadminSection, string> = {
    companies: "Companies",
    offices: "Offices",
    eaterGroups: "Eater groups",
    users: "Users",
};

/** Breadcrumbs: section list › company › office › eater group (as far as the hierarchy is known). */
export function hierarchyCrumbs(
    data: SysadminData,
    section: SysadminSection,
    parents: { companyId?: string; officeId?: string; eaterGroupId?: string },
): Crumb[] {
    const eaterGroup = data.eaterGroupById(parents.eaterGroupId);
    const office = data.officeById(parents.officeId ?? eaterGroup?.officeId);
    const company = data.companyById(parents.companyId ?? office?.companyId);
    const crumbs: Crumb[] = [{ label: sectionLabels[section], to: sysadminPaths.list(section) }];
    if (company) crumbs.push({ label: company.companyName || "Unnamed company", to: sysadminPaths.detail("companies", company.id) });
    if (office) crumbs.push({ label: office.officeName || "Unnamed office", to: sysadminPaths.detail("offices", office.id) });
    if (eaterGroup) crumbs.push({ label: eaterGroup.eaterGroupName || "Unnamed eater group", to: sysadminPaths.detail("eaterGroups", eaterGroup.id) });
    return crumbs;
}

interface VersionRow {
    version: string;
    versionDateTime: Date;
    creationUser: string;
    deleted: boolean;
    name: string;
    isLatest: boolean;
}

interface Props<T> {
    section: SysadminSection;
    label: string;
    idParam: string;
    getVersions: (id: string) => Promise<AxiosResponse<IVersionedObject<T>[]>>;
    nameOf: (content: T) => string;
    parentsOf?: (content: T) => { companyId?: string; officeId?: string; eaterGroupId?: string };
    /** Maps the id in the URL to the id used by the API (e.g. email → PlanEat user id). */
    resolveId?: (data: SysadminData, param: string) => string | undefined;
}

export function VersionsPage<T>({ section, label, idParam, getVersions, nameOf, parentsOf, resolveId }: Props<T>) {
    const params = useParams<Record<string, string>>();
    const id = safeDecode(params[idParam]);
    const history = useHistory();
    const data = useSysadminData();
    const apiId = resolveId ? resolveId(data, id) : id;
    const unresolved = !apiId && !data.loading;
    const [versions, setVersions] = useState<IVersionedObject<T>[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>();

    const load = useCallback(async () => {
        if (!apiId) return;
        setLoading(true);
        setError(undefined);
        try {
            const response = await getVersions(apiId);
            setVersions(response.data);
        } catch (e) {
            setError(errorMessage(e, "Could not load versions"));
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiId]);

    useEffect(() => {
        load();
    }, [load]);

    const sorted = [...versions].sort((a, b) => (parseServiceDate(b.versionDateTime)?.getTime() ?? 0) - (parseServiceDate(a.versionDateTime)?.getTime() ?? 0));
    const rows: VersionRow[] = sorted.map((v, index) => ({
        version: v.version,
        versionDateTime: v.versionDateTime,
        creationUser: v.creationUser,
        deleted: !!v.deletionTime,
        name: nameOf(v.content),
        isLatest: index === 0,
    }));
    const latest = sorted[0];
    const name = latest ? nameOf(latest.content) : "";

    const columns: GridColDef[] = [
        { field: "versionDateTime", headerName: "Changed", width: 170, valueFormatter: p => formatDateTime(p.value as any) },
        { field: "creationUser", headerName: "Changed by", flex: 1, minWidth: 200 },
        { field: "name", headerName: "Name", flex: 1, minWidth: 180 },
        {
            field: "isLatest", headerName: "Status", width: 130, sortable: false,
            renderCell: p => {
                const row = p.row as VersionRow;
                if (row.deleted) return <Chip size="small" label="Deleted" color="secondary" />;
                if (row.isLatest) return <Chip size="small" label="Current" color="primary" />;
                return <></>;
            },
        },
        { field: "version", headerName: "Version id", width: 300, hide: true },
    ];

    const crumbs = [
        ...hierarchyCrumbs(data, section, latest && parentsOf ? parentsOf(latest.content) : {}),
        { label: name || label, to: sysadminPaths.detail(section, id) },
        { label: "History" },
    ];

    return (
        <div>
            <PageHeader title={`History for ${name || label.toLowerCase()}`} subtitle="Select a version to see it and restore it" breadcrumbs={crumbs} />
            <EntityTable<VersionRow>
                rows={rows}
                columns={columns}
                getRowId={row => row.version}
                loading={loading && !unresolved}
                error={unresolved ? `No ${label.toLowerCase()} found` : error}
                onRetry={unresolved ? data.reload : load}
                emptyText="No versions found"
                onRowClick={row => history.push(sysadminPaths.version(section, id, row.version))}
            />
        </div>
    );
}
